export const MARKETPLACE_SOURCE = 'marketplace';

export const MARKETPLACE_CURRENCY_CODE = 'USD';

export const MARKETPLACE_PRODUCT_ID_PREFIX = 'marketplace_';

export const MARKETPLACE_FULFILLMENT_CENTER_ID = 'marketplace';

export const MARKETPLACE_FULFILLMENT_CENTER_NAME = 'Marketplace';

export const MARKETPLACE_CAMPAIGN_ID = 'marketplace';

export enum ItemSourceEnum {
  IeWarehouse = 'ie_warehouse',
  Marketplace = 'marketplace',
}

export const isMarketplaceSourced = <T>(item: T): item is T & { source: typeof MARKETPLACE_SOURCE } =>
  (item as { source?: string })?.source === MARKETPLACE_SOURCE;

// Mirrors `IMskuOption` so marketplace items flow through the same UI as MSKU inventory.
export interface IMarketplaceSKUOption {
  item_id: string;
  uid: string;
  name: string;
  product_sku: string;
  price: number;
  currency_code: string;
  count: number;
  color?: string;
  size?: string;
  other_option?: string;
  image_url?: string;
  provider_id?: string;
}

/** `name` is shown to senders, not recipients. */
export interface IMarketplaceSupplier {
  code: string;
  name?: string;
}

// Field names align with `IInventoryItem`: BE maps the unified product response to that shape.
export interface IMarketplaceItem {
  item_id: string;
  name: string;
  description: string;
  image_url: string;
  price: number;
  currency_code: string;
  count: number;
  source: typeof MARKETPLACE_SOURCE;
  marketplace_product_id: string;
  /**
   * Category *names* (IEOS `enrichment.categoryL1`), mirroring `IInventoryItem`. Not
   * `IMarketplaceCategory[]` — that is the counted facet shape from `search/facets`,
   * which only the category-list response carries.
   */
  categories: string[];
  provider_id?: string;
  vendor?: string;
  sku_options?: IMarketplaceSKUOption[];
  /** Top of the catalog price band. Cards show `price` (the band's floor) until a variant is picked. */
  max_price?: number;
  /** enrichment.categoryPath — carried for breadcrumbs on the details surface. */
  category_path?: string;
  /** IEOS lifecycle status: ACTIVE, DRAFT, ARCHIVED, DISCONTINUED. */
  status?: string;
  /** `undefined` when not yet known (a draft saved before suppliers were exposed); `null` when IEOS has none. */
  supplier?: IMarketplaceSupplier | null;
}

export interface IMarketplaceItemDetails extends IMarketplaceItem {
  images?: string[];
  supplier_code?: string;
  supplier_name?: string;
}

export interface IGiftBudget {
  value: number;
  currency: typeof MARKETPLACE_CURRENCY_CODE;
  used_budget?: number;
}

/** One entry of the send request's `marketplace` map (contract §4.1-4.8). */
export interface IMarketplaceRequestItem {
  product_id: string;
  name: string;
  brand?: string;
  price: number;
  currency: string;
  image_url: string;
  description: string;
  quantity: number;
}

export type TMarketplaceRequestMap = Record<string, IMarketplaceRequestItem>;

export interface IMarketplaceSendItem extends Omit<IMarketplaceRequestItem, 'image_url' | 'description'> {
  image_url?: string;
  description?: string;
  fulfillment_center_id?: string;
}

export type TMarketplaceSendMap = Record<string, IMarketplaceSendItem>;

export interface IMarketplaceSendSource {
  pick_campaign_ids?: readonly string[] | null;
  marketplace?: TMarketplaceSendMap | null;
}

export interface IMarketplaceMarkedItem {
  item_id?: string | null;
  fulfillment_center_id?: string | readonly string[] | null;
}

export interface IItemSourceEvidence extends IMarketplaceSendSource {
  pre_created_engagement_id?: string | null;
  gift_budget?: IGiftBudget | null;
  items?: readonly IMarketplaceMarkedItem[] | null;
}

export interface IFetchMarketplaceItemsRequestPayload {
  search_query?: string;
  category?: string;
  page: number;
  page_size: number;
}

export interface IFetchMarketplaceItemsResponse {
  items: IMarketplaceItem[];
  total_count: number;
}

export interface IFetchMarketplaceCategoriesResponse {
  categories: IMarketplaceCategory[];
}

export interface IFetchMarketplaceItemDetailsResponse {
  product: IMarketplaceItemDetails;
}

/** A category facet as returned by the BE, sourced from IEOS `search/facets`. */
export interface IMarketplaceCategory {
  value: string;
  count: number;
}

/**
 * A catalog card exactly as `POST /v1/marketplace/catalog` returns it (Go
 * `entities.MarketplaceItem`). Kept distinct from `IMarketplaceItem`, which mirrors
 * `IInventoryItem` so marketplace picks reuse the inventory UI; `mapCatalogItem` bridges them.
 *
 * Absent upstream: `source` (the FE stamps it — `isMarketplaceSourced` depends on it), a
 * single `price` (only a min/max band), and any per-variant data.
 */
export interface IMarketplaceCatalogItemResponse {
  id: string;
  title: string;
  primary_image_url: string;
  min_price: number;
  max_price: number;
  price_currency: string;
  /** IEOS lifecycle status: ACTIVE, DRAFT, ARCHIVED, DISCONTINUED. */
  status: string;
  max_inventory: number;
  /** enrichment.categoryL1 */
  category: string;
  /** enrichment.categoryPath */
  category_path: string;
  /**
   * Provider-dependent names for the same shop. `mapCatalogItem` collapses `brand` — the name a
   * recipient recognises — then `vendor` onto `IMarketplaceItem.vendor`; with neither present the
   * UI falls back to "Marketplace".
   */
  vendor: string;
  brand: string;
  /** Never part of the brand fallback chain. */
  supplier_name: string;
  supplier_code?: string;
}

export interface IFetchMarketplaceCatalogResponse {
  items: IMarketplaceCatalogItemResponse[];
  total_count: number;
}
